import { useMemo, useState } from "react";
import { PlusIcon } from "../../../assets/icons";
import CommonButton from "../../../components/commonButton";
import { useDispatch } from "react-redux";
import { deleteFeature, toggleFeatureStatus } from "../../../redux/slice/subscriptionSlice";
import { useToast } from "../../../context/TostContext";
import DeletePopup from "../../../components/delete-popup";
import ConfirmModal from "../../../components/withModal/ConfirmModal";
import { usePermission } from "../../../hooks/usePermission";
import FeatureModal from "../../../components/modals/subscription/FeatureModal";
import { TableSearch } from "../../../components/form/Fields";
import CustomTable from "../../../components/table/CustomTable";
import StatusChip from "../../../components/statusChip/StatusChip";

const MODULE = "subscription_plans";

const List = ({
    data,
    searchTerm,
    setSearchTerm,
    currentPage,
    limit,
    handleEdit,
    handleAdd,
    showModal,
    setShowModal,
    selectedItem,
    onSubmit,
    pagination,
}) => {
    const dispatch = useDispatch();
    const toast = useToast();
    const { hasPermission } = usePermission();

    const [openDeleteModal, setOpenDeleteModal] = useState(false);
    const [selectedId, setSelectedId] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);

    const [showStatusModal, setShowStatusModal] = useState(false);
    const [statusItem, setStatusItem] = useState(null);

    const handleConfirmDelete = async () => {
        if (!selectedId) return;
        try {
            setIsDeleting(true);
            const result = await dispatch(deleteFeature(selectedId)).unwrap();
            if (result.res.success) {
                toast.success(result.res.message || "Feature deleted");
                setOpenDeleteModal(false);
                setSelectedId(null);
            }
        } catch (err) {
            toast.error(err?.message || "Failed to delete");
        } finally {
            setIsDeleting(false);
        }
    };

    const handleConfirmStatusChange = async () => {
        if (!statusItem) return;
        const newStatus = statusItem.status === "active" ? "inactive" : "active";
        try {
            const result = await dispatch(toggleFeatureStatus({ id: statusItem.id, status: newStatus })).unwrap();
            if (result.res.success) {
                toast.success(result.res.message || "Status updated");
            }
        } catch (err) {
            toast.error(err?.message || "Failed to update status");
        } finally {
            setShowStatusModal(false);
            setStatusItem(null);
        }
    };

    const columns = useMemo(() => [
        {
            id: "sr_no",
            header: "SR NO.",
            cell: (_, row, index) => (currentPage - 1) * limit + index + 1
        },
        {
            id: "name",
            header: "FEATURE NAME",
            cell: (_, row) => row?.name || "-",
            className: "text-start"
        },
        {
            id: "code",
            header: "CODE",
            cell: (_, row) => row?.code || "-"
        },
        {
            id: "value_type",
            header: "VALUE TYPE",
            cell: (_, row) => row?.value_type || "-",
            className: "text-capitalize"
        },
        {
            id: "status",
            header: "STATUS",
            cell: (_, row) => (
                <StatusChip
                    bg={row?.status === "active" ? "success" : "danger"}
                    pill
                    title={row?.status === "active" ? "Active" : "Inactive"}
                    className={hasPermission(MODULE, "update") ? "cursor-pointer" : ""}
                    onClick={() => {
                        if (hasPermission(MODULE, "update")) {
                            setStatusItem(row);
                            setShowStatusModal(true);
                        }
                    }}
                />
            )
        }
    ], [currentPage, limit, hasPermission]);

    const actionList = useMemo(() => [
        hasPermission(MODULE, "read") && {
            title: "View",
            handler: (row) => handleEdit(row)
        },
        hasPermission(MODULE, "update") && {
            title: "Edit",
            handler: (row) => handleEdit(row)
        },
        hasPermission(MODULE, "delete") && {
            title: "Delete",
            handler: (row) => {
                setSelectedId(row.id);
                setOpenDeleteModal(true);
            }
        }
    ].filter(Boolean), [hasPermission, handleEdit]);

    return (
        <>
            <div className="p-4 d-flex align-items-center justify-content-between gap-4">
                <TableSearch value={searchTerm}
                    placeholder="Search Features"
                    onChange={(e) => setSearchTerm(e.target.value)} />
                {hasPermission(MODULE, "create") && (
                    <CommonButton
                        icon={<PlusIcon />}
                        variant="primary"
                        title="Add Feature"
                        onClick={handleAdd}
                    />
                )}
            </div>

            <CustomTable
                data={data}
                columns={columns}
                emptyMessage="No subscription features found"
                keyField="id"
                actionList={actionList}
                pagination={pagination}
            />

            <FeatureModal
                isOpen={showModal}
                onClose={() => setShowModal(false)}
                selectedItem={selectedItem}
                onSubmit={onSubmit}
                title={selectedItem ? "Edit Feature" : "Add Feature"}
                size="lg"
            />

            <DeletePopup
                show={openDeleteModal}
                onHide={() => {
                    setOpenDeleteModal(false);
                    setSelectedId(null);
                }}
                content="Are you sure you want to delete this feature?"
                removeTitle="Cancel"
                onConfirm={handleConfirmDelete}
                disabled={isDeleting}
                saveTitle={isDeleting ? "Deleting..." : "Delete"}
            />

            <ConfirmModal
                isOpen={showStatusModal}
                onClose={() => {
                    setShowStatusModal(false);
                    setStatusItem(null);
                }}
                title={statusItem?.status === "active" ? "Deactivate Feature" : "Activate Feature"}
                message={
                    statusItem?.status === "active"
                        ? "This feature will be deactivated. Do you want to continue?"
                        : "This feature will be activated. Do you want to continue?"
                }
                onConfirm={handleConfirmStatusChange}
            />
        </>
    );
};

export default List;
